import { type HTMLAttributes, type Ref } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "cn"

const badgeVariants = cva(
  "inline-flex h-fit shrink-0 items-center justify-center rounded-full font-medium whitespace-nowrap",
  {
    variants: {
      color: {
        primary: "bg-primary text-primary-foreground",
        muted: "bg-foreground/5 text-foreground/70",
        /* removing use of backdrop-blur-2xs causing severe chrome rendering issues */
        overlay: "bg-background/90 text-foreground",
        "overlay-light": "bg-white-a-4 text-white backdrop-blur-sm",
        "overlay-dark": "bg-black-a-9 text-white backdrop-blur-sm",
        accent: "bg-accent text-accent-foreground",
        sandbox: "bg-sandbox text-sandbox-foreground",
        red: "bg-red-10 text-red-1",
        sky: "bg-sky-11 text-sky-1",
        cyan: "bg-cyan-10 text-cyan-1",
        green: "bg-green-10 text-green-1",
        amber: "bg-amber-10 text-amber-1",
        plum: "bg-plum-10 text-plum-1",
        blue: "bg-blue-10 text-blue-1",
        gray: "bg-gray-3 text-gray-11",
        yellow: "bg-yellow-5 text-yellow-12",
        gold: "bg-gold-9 text-gold-1",
        "light-yellow": "border border-yellow-6 bg-yellow-2 text-yellow-11",
        black: "bg-black text-white",
      },
      size: {
        1: "h-4 min-w-4 text-3xs leading-none",
        2: "h-5 min-w-5 text-2xs leading-none",
      },
      circle: {
        true: "justify-center",
        false: "px-[.625em]",
      },
    },
    compoundVariants: [
      {
        size: 1,
        circle: true,
        className: "w-4",
      },
      {
        size: 2,
        circle: true,
        className: "w-5",
      },
    ],
    defaultVariants: {
      color: "primary",
      size: 1,
      circle: false,
    },
  }
)

export type BadgeColor = NonNullable<
  VariantProps<typeof badgeVariants>["color"]
>

export interface BadgeProps
  extends
    Omit<HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof badgeVariants> {}

function Badge({
  ref,
  className,
  color,
  size,
  circle,
  children,
  ...props
}: BadgeProps & { ref?: Ref<HTMLSpanElement> }) {
  const childrenStr = String(children)
  const isSingleChar = childrenStr.length === 1
  const shouldCircle = circle ?? isSingleChar

  return (
    <span
      ref={ref}
      data-slot="badge"
      className={cn(
        badgeVariants({ color, size, circle: shouldCircle }),
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}
Badge.displayName = "Badge"

export { Badge, badgeVariants }
